import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  EmojiEvents,
  SportsScore,
  CalendarToday,
  ShowChart,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { User } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { format } from 'date-fns';
import { getAvatarUrl, isDefaultAvatar } from '../../utils/avatar';

interface UserMatchesData {
  user_id: number;
  total_matches: number;
  completed_matches: number;
  upcoming_matches: number;
  matches: Array<{ match_id: number; date: string | null; status: string }>;
}

interface UserPerformanceData {
  user_id: number;
  win_rate: number;
  total_matches: number;
  total_wins: number;
  total_losses: number;
  average_score: number;
  performance: {
    current_streak: number;
    best_streak: number;
    recent_matches: Array<{
      match_id: number;
      date: string;
      won: boolean;
      team1_score: number;
      team2_score: number;
      user_team: number;
      winner_team: number;
    }>;
  };
}

export const AdminUserProgressPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [matchesData, setMatchesData] = useState<UserMatchesData | null>(null);
  const [performanceData, setPerformanceData] = useState<UserPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser?.is_admin || !userId) return;
    const id = parseInt(userId, 10);
    if (Number.isNaN(id)) {
      setError('Invalid user ID');
      setLoading(false);
      return;
    }
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const [userRes, matchesRes, perfRes] = await Promise.all([
          apiService.getUser(id),
          apiService.getStatisticsUserMatches(id),
          apiService.getStatisticsUserPerformance(id),
        ]);
        setUser(userRes.data);
        setMatchesData(matchesRes.data);
        setPerformanceData(perfRes.data);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          'Failed to load user progress';
        setError(String(msg));
        setUser(null);
        setMatchesData(null);
        setPerformanceData(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [currentUser?.is_admin, userId]);

  const matchesOverTimeData = useMemo(() => {
    if (!matchesData?.matches?.length) return [];
    const withDate = matchesData.matches
      .filter((m) => m.date != null)
      .map((m) => ({ ...m, dateParsed: new Date(m.date!) }));
    withDate.sort((a, b) => a.dateParsed.getTime() - b.dateParsed.getTime());
    const byDate = new Map<string, number>();
    for (const m of withDate) {
      const key = format(m.dateParsed, 'yyyy-MM-dd');
      byDate.set(key, (byDate.get(key) ?? 0) + 1);
    }
    const sortedDates = Array.from(byDate.entries()).sort(
      (a, b) => a[0].localeCompare(b[0])
    );
    let cumulative = 0;
    return sortedDates.map(([date, count]) => {
      cumulative += count;
      return {
        date,
        matches: count,
        cumulative,
        label: format(new Date(date), 'dd MMM yyyy'),
      };
    });
  }, [matchesData]);

  const winLossChartData = useMemo(() => {
    if (!performanceData) return [];
    return [
      {
        name: t('adminUserProgress.wins'),
        value: performanceData.total_wins,
        fill: '#2e7d32',
      },
      {
        name: t('adminUserProgress.losses'),
        value: performanceData.total_losses,
        fill: '#d32f2f',
      },
    ].filter((d) => d.value > 0);
  }, [performanceData, t]);

  if (!currentUser?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">{t('adminUserProgress.accessDenied')}</Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner message={t('adminUserProgress.loading')} />;
  }

  if (error || !user) {
    return (
      <Box p={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/users')}>
          {t('adminUserProgress.backToUsers')}
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error ?? t('adminUserProgress.userNotFound')}
        </Alert>
      </Box>
    );
  }

  const currentElo = user.elo_rating ?? user.initial_elo ?? 0;
  const initialElo = user.initial_elo ?? 0;

  return (
    <Box sx={{ p: 2 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/admin/users')}
        sx={{ mb: 2 }}
      >
        {t('adminUserProgress.backToUsers')}
      </Button>

      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Avatar
          src={getAvatarUrl(user.avatar_url) || undefined}
          sx={{ width: 64, height: 64 }}
        >
          {isDefaultAvatar(user.avatar_url) ? user.name?.charAt(0)?.toUpperCase() || '?' : null}
        </Avatar>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {t('adminUserProgress.title')}: {user.name}
          </Typography>
          <Typography color="text.secondary">{user.email}</Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <ShowChart color="primary" />
                <Typography color="text.secondary" variant="body2">
                  {t('adminUserProgress.currentElo')}
                </Typography>
              </Box>
              <Typography variant="h4">{currentElo.toFixed(1)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <TrendingUp color="primary" />
                <Typography color="text.secondary" variant="body2">
                  {t('adminUserProgress.initialElo')}
                </Typography>
              </Box>
              <Typography variant="h4">{initialElo.toFixed(1)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <SportsScore color="primary" />
                <Typography color="text.secondary" variant="body2">
                  {t('adminUserProgress.totalMatches')}
                </Typography>
              </Box>
              <Typography variant="h4">{matchesData?.total_matches ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('adminUserProgress.completed')}: {matchesData?.completed_matches ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <EmojiEvents color="primary" />
                <Typography color="text.secondary" variant="body2">
                  {t('adminUserProgress.winRate')}
                </Typography>
              </Box>
              <Typography variant="h4">
                {(performanceData?.win_rate ?? user.win_rate ?? 0).toFixed(1)}%
              </Typography>
              {performanceData && (performanceData.performance.current_streak > 0 || performanceData.performance.best_streak > 0) && (
                <Typography variant="caption" color="text.secondary">
                  {t('adminUserProgress.currentStreak')}: {performanceData.performance.current_streak} · {t('adminUserProgress.bestStreak')}: {performanceData.performance.best_streak}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {matchesOverTimeData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={t('adminUserProgress.matchesOverTime')}
            avatar={<CalendarToday />}
          />
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={matchesOverTimeData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number) => [value, t('adminUserProgress.cumulativeMatches')]}
                  labelFormatter={(label) => label}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="cumulative"
                  name={t('adminUserProgress.cumulativeMatches')}
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {winLossChartData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={t('adminUserProgress.winsVsLosses')}
            avatar={<EmojiEvents />}
          />
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={winLossChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name={t('adminUserProgress.count')}>
                  {winLossChartData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {performanceData?.performance?.recent_matches?.length ? (
        <Card>
          <CardHeader
            title={t('adminUserProgress.recentMatches')}
            avatar={<SportsScore />}
          />
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('adminUserProgress.date')}</TableCell>
                  <TableCell>{t('adminUserProgress.result')}</TableCell>
                  <TableCell align="right">{t('adminUserProgress.score')}</TableCell>
                  <TableCell align="right">{t('adminUserProgress.viewMatch')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {performanceData.performance.recent_matches.map((m) => (
                  <TableRow key={m.match_id}>
                    <TableCell>
                      {format(new Date(m.date), 'dd MMM yyyy')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={m.won ? t('adminUserProgress.won') : t('adminUserProgress.lost')}
                        color={m.won ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {m.team1_score} – {m.team2_score}
                    </TableCell>
                    <TableCell align="right">
                      <Link
                        component={RouterLink}
                        to="/admin/matches"
                        state={{ viewMatchId: m.match_id }}
                        underline="hover"
                      >
                        {t('adminUserProgress.viewMatch')}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        matchesData?.matches?.length === 0 && (
          <Card>
            <CardContent>
              <Typography color="text.secondary">{t('adminUserProgress.noMatches')}</Typography>
            </CardContent>
          </Card>
        )
      )}
    </Box>
  );
};
